import { ToastType } from '../../../services/toast.ts';

export class ToastItem extends HTMLElement {
    private message: string = '';
    private type: ToastType = 'info';
    private duration: number = 5000;
    private timer: number | null = null;

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
    }

    static get observedAttributes() {
        return ['message', 'type', 'duration'];
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (oldValue === newValue) return;

        if (name === 'message') this.message = newValue;
        if (name === 'type') this.type = newValue as ToastType;
        if (name === 'duration') this.duration = parseInt(newValue, 10) || 5000;
    }

    connectedCallback() {
        this.render();
        this.startTimer();
    }

    disconnectedCallback() {
        if (this.timer) clearTimeout(this.timer);
    }

    private startTimer() {
        if (this.duration > 0) {
            this.timer = setTimeout(() => {
                this.dismiss();
            }, this.duration);
        }
    }

    private dismiss() {
        this.classList.add('dismissing');
        this.addEventListener('transitionend', () => {
            this.dispatchEvent(new CustomEvent('remove', { bubbles: true, composed: true }));
        }, { once: true });

        // Fallback if transition event doesn't fire (e.g. detached)
        setTimeout(() => {
            this.dispatchEvent(new CustomEvent('remove', { bubbles: true, composed: true }));
        }, 400);
    }

    render() {
        if (!this.shadowRoot) return;

        const icon = this.getIcon();

        this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          margin-bottom: 0.75rem;
          transition: all 0.3s ease;
          opacity: 0;
          transform: translateX(20px);
          animation: slideIn 0.3s forwards;
          width: 320px;
        }

        :host(.dismissing) {
          opacity: 0;
          transform: translateX(20px);
          margin-bottom: -3rem; /* Collapse space */
        }

        @keyframes slideIn {
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .toast {
          display: grid;
          grid-template-columns: auto 1fr auto;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem;
          background: #141414;
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-left: 4px solid var(--toast-color, #475569);
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
          color: white;
          font-family: var(--font-main, sans-serif);
          position: relative;
          overflow: hidden;
        }

        /* Type Variants */
        .toast.success { --toast-color: #10b981; }
        .toast.error { --toast-color: #ef4444; }
        .toast.warning { --toast-color: #f59e0b; }
        .toast.info { --toast-color: #3b82f6; }

        .icon {
          display: grid;
          place-items: center;
          color: var(--toast-color);
        }

        .content {
          font-size: 0.9rem;
          line-height: 1.4;
        }

        .close-btn {
          background: transparent;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          font-size: 1.25rem;
          padding: 0;
          line-height: 1;
          display: grid;
          place-items: center;
          transition: color 0.2s;
        }

        .close-btn:hover {
          color: white;
        }
      </style>

      <div class="toast ${this.type}">
        <div class="icon">${icon}</div>
        <div class="content">${this.message}</div>
        <button class="close-btn" aria-label="Close">&times;</button>
      </div>
    `;

        this.shadowRoot.querySelector('.close-btn')?.addEventListener('click', () => {
            this.dismiss();
        });
    }

    private getIcon() {
        switch (this.type) {
            case 'success':
                return `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>`;
            case 'error':
                return `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>`;
            case 'warning':
                return `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>`;
            default: // info
                return `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>`;
        }
    }
}

customElements.define('toast-item', ToastItem);
